import type { OnsetEnvelope } from './analysis';
import type { AnalyseRequest, WorkerMessage } from './trackAnalysis.worker';

/**
 * Main-thread side of whole-track analysis: prepare the audio, drive the
 * worker, hand back something the loop finder can draw.
 */

/**
 * Onset detection doesn't need full bandwidth, and a 96 kHz decode makes four
 * times as many FFT frames as it needs to. Everything downstream works in
 * seconds, so the rate change is invisible past this point.
 */
const ANALYSIS_RATE = 22050;

export interface TrackAnalysis {
  bpm: number;
  offset: number;
  confidence: number;
  transients: number[];
  peaks: Float32Array;
  envelope: OnsetEnvelope;
}

export interface Progress {
  stage: string;
  fraction: number;
}

/** Mono downmix plus box-filter decimation to `ANALYSIS_RATE`. */
export function monoForAnalysis(buffer: AudioBuffer): { data: Float32Array; rate: number } {
  const channels = buffer.numberOfChannels;
  const ratio = buffer.sampleRate / ANALYSIS_RATE;
  if (ratio <= 1.01) {
    const out = new Float32Array(buffer.length);
    for (let c = 0; c < channels; c++) {
      const d = buffer.getChannelData(c);
      for (let i = 0; i < out.length; i++) out[i] += d[i];
    }
    for (let i = 0; i < out.length; i++) out[i] /= channels;
    return { data: out, rate: buffer.sampleRate };
  }

  const outLen = Math.max(1, Math.floor(buffer.length / ratio));
  const out = new Float32Array(outLen);
  const data: Float32Array[] = [];
  for (let c = 0; c < channels; c++) data.push(buffer.getChannelData(c));

  // Averaging across the decimation window is a crude but adequate anti-alias
  // filter — without it, hi-hat energy folds down and invents onsets.
  for (let i = 0; i < outLen; i++) {
    const s = Math.floor(i * ratio);
    const e = Math.min(buffer.length, Math.floor((i + 1) * ratio));
    let acc = 0;
    let n = 0;
    for (let j = s; j < e; j++) {
      for (let c = 0; c < channels; c++) acc += data[c][j];
      n += channels;
    }
    out[i] = n ? acc / n : 0;
  }
  return { data: out, rate: ANALYSIS_RATE };
}

export function analyseTrack(
  buffer: AudioBuffer,
  beatsPerBar: number,
  onProgress?: (p: Progress) => void,
): Promise<TrackAnalysis> {
  const { data, rate } = monoForAnalysis(buffer);
  // Wider buckets for longer files, so the overview stays readable when zoomed.
  const peakBuckets = Math.min(16384, Math.max(2048, Math.round(buffer.duration * 40)));

  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./trackAnalysis.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
      const msg = e.data;
      if (msg.type === 'progress') {
        onProgress?.({ stage: msg.stage, fraction: msg.fraction });
        return;
      }
      worker.terminate();
      if (msg.type === 'error') {
        reject(new Error(msg.message));
        return;
      }
      resolve({
        bpm: msg.bpm,
        offset: msg.offset,
        confidence: msg.confidence,
        transients: msg.transients,
        peaks: msg.peaks,
        envelope: {
          values: msg.envelope,
          low: msg.envelopeLow,
          rate: msg.envelopeRate,
          origin: msg.envelopeOrigin,
        },
      });
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message || 'Analysis worker failed'));
    };

    const req: AnalyseRequest = {
      mono: data,
      sampleRate: rate,
      duration: buffer.duration,
      beatsPerBar,
      peakBuckets,
    };
    worker.postMessage(req, [data.buffer]);
  });
}
